import { FunctionComponent, useState, useEffect, useRef } from 'react';
import { format as formatDateFns } from 'date-fns';
import classNames from 'classnames/bind';
import { Matcher } from 'react-day-picker';
import SVG from 'react-inlinesvg';
import { getInputBorderStyle, getInputStyle } from 'lib/BaseInput';
import { InputSize } from 'components/common/types/Input.types';
import chevronDown from 'assets/icons/arrow_light_down.svg';
import closeSvg from 'assets/icons/close.svg';
import calendar from 'assets/icons/calendar.svg';
import usePrevious from 'customHooks/usePrevious';
import { DATE_FORMAT } from 'modules/api/constants';
import { areDatesEqual } from 'utils/dateUtil';
import { formatDateToFnsDate } from 'utils/dateFormatUtil';
import { FIRST_DAY_OF_THE_WEEK } from 'consts/dateTimePicker';
import styles from './DayPicker.pcss';
import DateTimePicker from '../DateTimePicker';

const cx = classNames.bind(styles);

const formatDate = (date: Date, dateFormat: string): string => {
  try {
    return formatDateFns(date, formatDateToFnsDate(dateFormat));
  } catch {
    return '';
  }
};

type Props = {
  dataTestId?: string;
  disabledDays?: Matcher[];
  align?: 'left' | 'right';
  format?: string;
  fromMonth?: Date;
  inputId?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  onDayChange: (date: Date | null) => void;
  placeholder?: string;
  selectedDay?: Date;
  toMonth?: Date;
  firstDayOfTheWeek?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
};

const DayPicker: FunctionComponent<Props> = ({
  dataTestId = '',
  disabledDays = [],
  format = DATE_FORMAT,
  inputId,
  fromMonth,
  toMonth,
  isClearable = false,
  isDisabled = false,
  align = 'left',
  onDayChange,
  placeholder = '',
  firstDayOfTheWeek = FIRST_DAY_OF_THE_WEEK,
  selectedDay,
}) => {
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevSelectedDay = usePrevious(selectedDay);

  const handleDaySelect = (day: Date): void => {
    if (!areDatesEqual(day, prevSelectedDay)) {
      onDayChange(day);
      setIsOverlayVisible(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOverlayVisible(false);
      }
    };

    if (isOverlayVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOverlayVisible]);

  const inputValue = selectedDay ? formatDate(selectedDay, format) : '';

  return (
    <div
      ref={containerRef}
      data-test-id={dataTestId}
      className={getInputBorderStyle({
        classNames: cx('dayPicker', 'DayPickerInput', { readOnly: isDisabled, readOnlyOpacity: isDisabled }),
      })}
      style={{ position: 'relative' }}
    >
      <SVG src={calendar} className={cx('svg-calendar')} />
      <input
        id={inputId}
        className={cx(getInputStyle({ size: InputSize.MEDIUM }), 'pl-0 cursor-pointer')}
        readOnly
        placeholder={placeholder}
        value={inputValue}
        onClick={() => !isDisabled && setIsOverlayVisible(!isOverlayVisible)}
      />
      {isClearable && selectedDay ? (
        <SVG src={closeSvg} className={cx('icon-right', 'svg-close')} onClick={() => onDayChange(null)} />
      ) : (
        <SVG src={chevronDown} className={cx('icon-right', 'svg-chevronDown')} />
      )}
      {isOverlayVisible && (
        <div
          className="DayPickerInput-Overlay"
          style={{ position: 'absolute', ...(align === 'right' ? { right: 0 } : { left: 0 }), top: '100%', zIndex: 50 }}
        >
          <DateTimePicker
            value={selectedDay}
            onChange={handleDaySelect}
            isDisabled={isDisabled}
            fromMonth={fromMonth}
            toMonth={toMonth}
            isTimePickerEnabled={false}
            disabledDays={disabledDays}
            firstDayOfTheWeek={firstDayOfTheWeek}
          />
        </div>
      )}
    </div>
  );
};

export default DayPicker;
